<template>
  <section class="vip-hero">
    <span class="label">{{ t('promo.vip.hero.currentLevel') }}</span>
    <button type="button" class="rule-btn" @click="emit('rules')">{{ t('promo.vip.hero.rules') }} ›</button>
    <div class="vip">
      <span class="vip-icon">
        <img v-if="userStore.vip.iconUrl" :src="userStore.vip.iconUrl" alt="" />
        <template v-else>👑</template> </span
      >V{{ status.currentLevel }}
    </div>
    <p v-if="isMax" class="hint">{{ t('promo.vip.hero.maxLevel') }}</p>
    <p v-else class="hint">
      {{ t('promo.vip.hero.nextLevel', { level: status.nextLevel }) }}
      <span class="hint-amount">{{ fmtAmt }}</span>
    </p>
    <div class="track">
      <i :style="{ width: `${percent}%` }" />
    </div>
    <div class="track-labels">
      <span>V{{ status.currentLevel }}</span>
      <span>V{{ status.nextLevel }}</span>
    </div>
  </section>
</template>

<script setup lang="ts">
import { formatBaseAmt } from '~/utils/transfer/money'
import type { VipStatus } from '../types'

const props = defineProps<{ status: VipStatus; percent?: number }>()
const emit = defineEmits<{ rules: [] }>()

const { t } = useI18n()
const userStore = useUserStore()
const fmtAmt = computed(() => formatBaseAmt(props.status.amountToNext, userStore.baseCurrency))
const percent = computed(() => props.percent ?? 0)
const isMax = computed(() => percent.value >= 100)
</script>

<style scoped lang="scss">
.vip-hero {
  position: relative;
  padding: 20px 18px 18px;
  border-radius: var(--r-xl);
  overflow: hidden;
  color: #fff;
  background: linear-gradient(135deg, #2c2244 0%, #191026 62%, #120a1e 100%);
  border: 1px solid rgba(255, 199, 107, 0.34);
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.vip-hero::before {
  content: '';
  position: absolute;
  top: -55px;
  right: -30px;
  width: 210px;
  height: 210px;
  pointer-events: none;
  background: radial-gradient(circle, rgba(255, 199, 107, 0.28), transparent 62%);
}

.rule-btn {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  font-size: 12px;
  font-weight: 700;
  color: #ffdf9c;
  background: rgba(255, 199, 107, 0.13);
  border: 1px solid rgba(255, 199, 107, 0.34);
  padding: 5px 12px;
  border-radius: var(--r-pill);
}

.label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.62);
  font-weight: 600;
  letter-spacing: 0.5px;
}

.level {
  display: block;
  margin-top: 6px;
  font-size: 38px;
  font-weight: 900;
  background: var(--grad-gold);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.vip {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 42px;
  font-weight: 900;
  line-height: 1;
  margin: 5px 0 11px;
  background: linear-gradient(#fff6db, #ffd579 52%, #e6a636);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.45));
}

.vip-icon {
  font-size: 27px;
  -webkit-text-fill-color: initial;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.4));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.hint {
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.78);
  position: relative;
  z-index: 1;
}

.hint-amount {
  color: #ffdf9c;
  font-weight: 900;
}

.track {
  height: 9px;
  border-radius: var(--r-xs);
  background: rgba(255, 255, 255, 0.13);
  overflow: hidden;
  margin: 13px 0 6px;
}

.track i {
  display: block;
  height: 100%;
  border-radius: var(--r-xs);
  background: var(--grad-gold);
  box-shadow: 0 0 10px rgba(255, 171, 60, 0.55);
}

.track-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 11px;
  color: var(--vip-rich-sub);
}
</style>
